// Task creation and edit form component
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { supabase } from '@/lib/supabase'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'

const taskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  category: z.string().min(1, 'Category is required'),
  tshirt_size_effort: z.enum(['S', 'M', 'L', 'XL']),
  tshirt_size_capital: z.enum(['S', 'M', 'L', 'XL']),
  frequency: z.string().min(1, 'Frequency is required'),
  founder_leverage: z.string().optional(),
})

type TaskFormData = z.infer<typeof taskSchema>

interface TaskFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  task?: {
    id: string
    title: string
    category: string
    tshirt_size_effort: string
    tshirt_size_capital: string
    frequency: string
    founder_leverage: string
  }
  onSuccess?: () => void
}

export default function TaskForm({ open, onOpenChange, task, onSuccess }: TaskFormProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { currentProject } = useProject()

  const form = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      title: task?.title || '',
      category: task?.category || '',
      tshirt_size_effort: (task?.tshirt_size_effort as 'S' | 'M' | 'L' | 'XL') || 'M',
      tshirt_size_capital: (task?.tshirt_size_capital as 'S' | 'M' | 'L' | 'XL') || 'M',
      frequency: task?.frequency || '',
      founder_leverage: task?.founder_leverage || '',
    },
  })

  const onSubmit = async (data: TaskFormData) => {
    setLoading(true)
    setError(null)

    try {
      if (task) {
        // Update existing task
        const { error } = await supabase
          .from('tasks')
          .update(data)
          .eq('id', task.id)

        if (error) throw error
      } else {
        // Create new task
        if (!currentProject) {
          throw new Error('No project selected')
        }
        
        const { error } = await supabase
          .from('tasks')
          .insert({
            ...data,
            project_id: currentProject.id
          })

        if (error) throw error
      }

      onSuccess?.()
      onOpenChange(false)
      form.reset()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const categories = [
    'Business Development',
    'Product Development',
    'Marketing',
    'Sales',
    'Operations',
    'Finance',
    'Legal',
    'HR',
    'Other',
  ]

  const frequencies = [
    'One-time',
    'Daily',
    'Weekly',
    'Monthly',
    'Quarterly',
    'Ongoing',
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{task ? 'Edit Task' : 'Create New Task'}</DialogTitle>
          <DialogDescription>
            Define a task that contributes to the company's success. Tasks are weighted by effort and capital requirements.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="title">Task Title</Label>
            <Input
              id="title"
              {...form.register('title')}
              placeholder="e.g., Develop MVP, Secure seed funding"
            />
            {form.formState.errors.title && (
              <p className="text-sm text-red-600">{form.formState.errors.title.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={form.watch('category')}
              onValueChange={(value) => form.setValue('category', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.category && (
              <p className="text-sm text-red-600">{form.formState.errors.category.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="effort">Effort Size</Label>
              <Select
                value={form.watch('tshirt_size_effort')}
                onValueChange={(value) => form.setValue('tshirt_size_effort', value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="S">Small (S)</SelectItem>
                  <SelectItem value="M">Medium (M)</SelectItem>
                  <SelectItem value="L">Large (L)</SelectItem>
                  <SelectItem value="XL">Extra Large (XL)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="capital">Capital Required</Label>
              <Select
                value={form.watch('tshirt_size_capital')}
                onValueChange={(value) => form.setValue('tshirt_size_capital', value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="S">Small (S)</SelectItem>
                  <SelectItem value="M">Medium (M)</SelectItem>
                  <SelectItem value="L">Large (L)</SelectItem>
                  <SelectItem value="XL">Extra Large (XL)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="frequency">Frequency</Label>
            <Select
              value={form.watch('frequency')}
              onValueChange={(value) => form.setValue('frequency', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                {frequencies.map((freq) => (
                  <SelectItem key={freq} value={freq}>
                    {freq}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.frequency && (
              <p className="text-sm text-red-600">{form.formState.errors.frequency.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="founder_leverage">Founder Leverage (Optional)</Label>
            <Textarea
              id="founder_leverage"
              {...form.register('founder_leverage')}
              placeholder="Describe any strategic advantage or special considerations for founders"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {task ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}